import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  Divider,
  Menu,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Instagram as InstagramIcon,
  Message as DMIcon,
  MoreVert as MoreIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { leadApi } from '../services/api';
import { useApp } from '../context/AppContext';
import { LoadingSpinner, StageChip, NicheChip, EmptyState } from '../components/common';
import {
  formatInstagramHandle,
  formatRelativeDate,
  formatNumber,
  getDaysInStage,
  getInstagramProfileUrl,
  getInstagramDmUrl,
  debounce,
  toCSV,
  downloadFile,
} from '../utils/helpers';
import { stageColors, nicheConfig } from '../theme';

const stages = Object.keys(stageColors);
const niches = Object.keys(nicheConfig);

const Leads = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSnackbar } = useApp();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, limit: 25, total: 0 });

  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [stageFilter, setStageFilter] = useState(searchParams.get('stage')?.split(',') || []);
  const [nicheFilter, setNicheFilter] = useState(searchParams.get('niche')?.split(',') || []);
  const [followupFilter, setFollowupFilter] = useState(searchParams.get('followup') || '');
  const [showClosedLeads, setShowClosedLeads] = useState(false);

  // Terminal statuses that are hidden by default
  const terminalStatuses = ['won', 'lost', 'not_interested'];
  
  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);

  // Bulk selection
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFollowupDate, setBulkFollowupDate] = useState(null);
  const [bulkFollowupNotes, setBulkFollowupNotes] = useState('');

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      // Determine which stages to filter
      let effectiveStageFilter = stageFilter;
      if (stageFilter.length === 0 && !showClosedLeads) {
        // No stage filter selected and closed leads hidden - show all non-terminal stages
        effectiveStageFilter = stages.filter(s => !terminalStatuses.includes(s));
      } else if (stageFilter.length > 0 && !showClosedLeads) {
        // Stage filter selected but closed leads hidden - exclude terminal from selection
        effectiveStageFilter = stageFilter.filter(s => !terminalStatuses.includes(s));
      }

      const params = {
        page: pagination.page + 1,
        limit: pagination.limit,
        search: search || undefined,
        stage: effectiveStageFilter.length > 0 ? effectiveStageFilter.join(',') : undefined,
        niche: nicheFilter.length > 0 ? nicheFilter.join(',') : undefined,
        hasFollowupToday: followupFilter === 'today' ? 'true' : undefined,
        hasFollowupThisWeek: followupFilter === 'week' ? 'true' : undefined,
        hasOverdueFollowup: followupFilter === 'overdue' ? 'true' : undefined,
      };

      const response = await leadApi.getAll(params);
      setLeads(response.data.leads);
      setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
    } catch (error) {
      showSnackbar('Failed to load leads', 'error');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, stageFilter, nicheFilter, followupFilter, showClosedLeads, showSnackbar]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Debounced search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value);
      setPagination(prev => ({ ...prev, page: 0 }));
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination(prev => ({ ...prev, limit: parseInt(event.target.value, 10), page: 0 }));
  };

  const handleMenuOpen = (event, lead) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedLead(lead);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedLead(null);
  };

  const handleDelete = async () => {
    if (!selectedLead) return;
    try {
      await leadApi.delete(selectedLead._id);
      showSnackbar('Lead deleted successfully', 'success');
      fetchLeads();
    } catch (error) {
      showSnackbar('Failed to delete lead', 'error');
    }
    handleMenuClose();
  };

  const handleExport = async () => {
    try {
      const response = await leadApi.exportLeads({
        stage: stageFilter.join(','),
        niche: nicheFilter.join(','),
      });
      const csv = toCSV(response.data, [
        { field: 'instagramHandle', label: 'Instagram Handle' },
        { field: 'businessName', label: 'Business Name' },
        { field: 'ownerName', label: 'Owner Name' },
        { field: 'email', label: 'Email' },
        { field: 'phone', label: 'Phone' },
        { field: 'niche', label: 'Niche' },
        { field: 'city', label: 'City' },
        { field: 'stateProvince', label: 'State' },
        { field: 'followerCount', label: 'Followers' },
        { field: 'stage', label: 'Stage' },
        { field: 'source', label: 'Source' },
      ]);
      downloadFile(csv, `leads-export-${new Date().toISOString().split('T')[0]}.csv`);
      showSnackbar('Leads exported successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to export leads', 'error');
    }
  };

  // Bulk selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLeads(leads.map(lead => lead._id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (event, leadId) => {
    event.stopPropagation();
    if (selectedLeads.includes(leadId)) {
      setSelectedLeads(selectedLeads.filter(id => id !== leadId));
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
    }
  };

  const handleBulkScheduleFollowup = async () => {
    if (!bulkFollowupDate) {
      showSnackbar('Please select a date', 'error');
      return;
    }

    try {
      await leadApi.bulkScheduleFollowup({
        leadIds: selectedLeads,
        date: bulkFollowupDate,
        notes: bulkFollowupNotes,
      });
      showSnackbar(`Follow-ups scheduled for ${selectedLeads.length} leads`, 'success');
      setBulkDialogOpen(false);
      setBulkFollowupDate(null);
      setBulkFollowupNotes('');
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      showSnackbar('Failed to schedule follow-ups', 'error');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStageFilter([]);
    setNicheFilter([]);
    setFollowupFilter('');
    setShowClosedLeads(false);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const hasFilters = search || stageFilter.length > 0 || nicheFilter.length > 0 || followupFilter || showClosedLeads;

  return (
    <Box>
      {/* Toolbar */}
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search leads..."
            size="small"
            defaultValue={search}
            onChange={handleSearchChange}
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
              sx: { fontSize: '0.8rem' },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Stage</InputLabel>
            <Select
              multiple
              value={stageFilter}
              onChange={(e) => {
                setStageFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 0 }));
              }}
              input={<OutlinedInput label="Stage" />}
              renderValue={(selected) => `${selected.length} selected`}
              sx={{ fontSize: '0.8rem' }}
            >
              {stages.map((stage) => (
                <MenuItem key={stage} value={stage} dense>
                  <Checkbox checked={stageFilter.includes(stage)} size="small" />
                  <ListItemText primary={stage.replace('_', ' ').toUpperCase()} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Niche</InputLabel>
            <Select
              multiple
              value={nicheFilter}
              onChange={(e) => {
                setNicheFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 0 }));
              }}
              input={<OutlinedInput label="Niche" />}
              renderValue={(selected) => `${selected.length} selected`}
              sx={{ fontSize: '0.8rem' }}
            >
              {niches.map((niche) => (
                <MenuItem key={niche} value={niche} dense>
                  <Checkbox checked={nicheFilter.includes(niche)} size="small" />
                  <ListItemText primary={`${nicheConfig[niche].emoji} ${nicheConfig[niche].label}`} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Follow-up</InputLabel>
            <Select
              value={followupFilter}
              onChange={(e) => {
                setFollowupFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 0 }));
              }}
              label="Follow-up"
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value="" dense>All</MenuItem>
              <MenuItem value="today" dense>Due Today</MenuItem>
              <MenuItem value="week" dense>This Week</MenuItem>
              <MenuItem value="overdue" dense>Overdue</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showClosedLeads}
                onChange={(e) => {
                  setShowClosedLeads(e.target.checked);
                  setPagination(prev => ({ ...prev, page: 0 }));
                }}
              />
            }
            label={<Typography sx={{ fontSize: '0.75rem' }}>Show Closed</Typography>}
            sx={{ ml: 0.5 }}
          />

          {hasFilters && (
            <Button
              size="small"
              startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
              onClick={clearFilters}
              sx={{ fontSize: '0.75rem' }}
            >
              Clear
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Button
            size="small"
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleExport}
            sx={{ fontSize: '0.75rem' }}
          >
            Export
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate('/leads/new')}
            sx={{ fontSize: '0.75rem' }}
          >
            Add Lead
          </Button>
        </Box>
      </Paper>

      {/* Bulk Actions Bar */}
      {selectedLeads.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 1.5, bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''} selected
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="inherit"
              startIcon={<CalendarIcon sx={{ fontSize: 16 }} />}
              onClick={() => setBulkDialogOpen(true)}
              sx={{ bgcolor: 'white', color: 'primary.main', fontSize: '0.75rem' }}
            >
              Schedule Follow-up
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton size="small" onClick={() => setSelectedLeads([])} sx={{ color: 'white' }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* Table */}
      <Paper>
        {loading ? (
          <LoadingSpinner message="Loading leads..." />
        ) : leads.length === 0 ? (
          <EmptyState
            title="No leads found"
            description={hasFilters ? "Try adjusting your filters" : "Add your first lead to get started"}
            actionLabel="Add Lead"
            onAction={() => navigate('/leads/new')}
          />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ py: 1 }}>
                      <Checkbox
                        size="small"
                        checked={selectedLeads.length === leads.length && leads.length > 0}
                        indeterminate={selectedLeads.length > 0 && selectedLeads.length < leads.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }}>Instagram</TableCell>
                    <TableCell sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }}>Business</TableCell>
                    <TableCell sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }}>Niche</TableCell>
                    <TableCell sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }}>Location</TableCell>
                    <TableCell sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }} align="right">Followers</TableCell>
                    <TableCell sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }}>Stage</TableCell>
                    <TableCell sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }}>Follow-up</TableCell>
                    <TableCell sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow
                      key={lead._id}
                      hover
                      selected={selectedLeads.includes(lead._id)}
                      sx={{ cursor: 'pointer', '& td': { py: 0.75 } }}
                      onClick={() => navigate(`/leads/${lead._id}`)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedLeads.includes(lead._id)}
                          onChange={(e) => handleSelectLead(e, lead._id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={600} color="primary.main" sx={{ fontSize: '0.85rem' }}>
                          {formatInstagramHandle(lead.instagramHandle)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontSize: '0.85rem' }}>{lead.businessName || '-'}</Typography>
                        {lead.ownerName && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {lead.ownerName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <NicheChip niche={lead.niche} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                          {[lead.city, lead.stateProvince].filter(Boolean).join(', ') || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                          {formatNumber(lead.followerCount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StageChip stage={lead.stage} />
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {getDaysInStage(lead.stageUpdatedAt)}d
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {lead.nextFollowupDate ? (
                          <Typography
                            variant="caption"
                            sx={{ fontSize: '0.8rem' }}
                            color={new Date(lead.nextFollowupDate) < new Date() ? 'error.main' : 'text.primary'}
                          >
                            {formatRelativeDate(lead.nextFollowupDate)}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
                          <Tooltip title="View Profile">
                            <IconButton
                              size="small"
                              sx={{ p: 0.5 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(getInstagramProfileUrl(lead.instagramHandle), '_blank');
                              }}
                            >
                              <InstagramIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send DM">
                            <IconButton
                              size="small"
                              sx={{ p: 0.5 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(getInstagramDmUrl(lead.instagramHandle), '_blank');
                              }}
                            >
                              <DMIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            sx={{ p: 0.5 }}
                            onClick={(e) => handleMenuOpen(e, lead)}
                          >
                            <MoreIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page}
              onPageChange={handlePageChange}
              rowsPerPage={pagination.limit}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
            />
          </>
        )}
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          navigate(`/leads/${selectedLead?._id}/edit`);
          handleMenuClose();
        }}>
          Edit Lead
        </MenuItem>
        <MenuItem onClick={() => {
          navigate(`/leads/${selectedLead?._id}`);
          handleMenuClose();
        }}>
          View Details
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          Delete Lead
        </MenuItem>
      </Menu>

      {/* Bulk Follow-up Dialog */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Schedule Bulk Follow-up</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Scheduling follow-up for {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''}
          </Alert>

          <DatePicker
            label="Follow-up Date"
            value={bulkFollowupDate}
            onChange={(date) => setBulkFollowupDate(date)}
            minDate={new Date()}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                sx: { mb: 2 }
              }
            }}
          />

          <TextField
            fullWidth
            label="Notes (optional)"
            value={bulkFollowupNotes}
            onChange={(e) => setBulkFollowupNotes(e.target.value)}
            multiline
            rows={2}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkScheduleFollowup}>
            Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Leads;
